import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

const LinkedinIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const socialLinks = [
  { icon: Facebook, href: '', label: 'Facebook' },
  { icon: LinkedinIcon, href: 'https://www.linkedin.com/in/joe-penwell-407bb9271', label: 'LinkedIn' },
  { icon: Instagram, href: '', label: 'Instagram' },
  { icon: Youtube, href: '', label: 'YouTube' },
].filter(s => s.href);

const Footer: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <footer style={{ background: 'var(--color-footer-bg)', color: 'white', padding: '3.5rem 2rem 2rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '2.5rem', marginBottom: '3rem' }}>

          {/* Brand */}
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: '0.75rem', color: 'white', textAlign: isMobile ? 'center' : 'left' }}>JVP Health Tech Solutions</div>
            <p style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.875rem', lineHeight: 1.7, margin: '0 0 1.25rem', textAlign: isMobile ? 'center' : 'left' }}>
              Professional healthcare equipment installation and support services.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: isMobile ? 'center' : 'flex-start' }}>
              {[
                { icon: Mail, value: 'info@jvphealthtechsolutions.com', href: 'mailto:info@jvphealthtechsolutions.com' },
                { icon: Phone, value: '(480) 828-5540', href: 'tel:+16024036905' },
                { icon: MapPin, value: 'Nationwide Coverage', href: null },
              ].map(({ icon: Icon, value, href }) => (
                <div key={value} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <div style={{ background: 'var(--color-text-label)', borderRadius: '8px', padding: '0.45rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={14} color="var(--color-primary-pale)" strokeWidth={2} />
                  </div>
                  {href
                    ? <a href={href} style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.875rem', textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-muted-on-dark)')}>{value}</a>
                    : <span style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.875rem' }}>{value}</span>
                  }
                </div>
              ))}
            </div>
          </div>

          {/* Pages */}
          <div>
            <h3 style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', color: 'var(--color-muted-on-dark)', textTransform: 'uppercase', marginBottom: '1rem', margin: '0 0 1rem' }}>Company</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {[
                { label: 'Home', to: '/' },
                { label: 'Products', to: '/products' },
                { label: 'Our Work', to: '/work' },
                { label: 'About Us', to: '/about' },
              ].map(link => (
                <Link key={link.to} to={link.to} style={{ color: 'var(--color-border-light)', fontSize: '0.9rem', textDecoration: 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-border-light)')}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', color: 'var(--color-muted-on-dark)', textTransform: 'uppercase', marginBottom: '1rem', margin: '0 0 1rem' }}>Services</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {[
                { label: 'Site Assessment & Planning', to: '/services/consulting' },
                { label: 'Equipment Trials & Demos', to: '/services/demos' },
                { label: 'Equipment & Installation', to: '/services/installation' },
                { label: 'Staff Training & Education', to: '/services/training' },
                { label: 'Support & Maintenance', to: '/services/maintenance' },
              ].map(link => (
                <Link key={link.to} to={link.to} style={{ color: 'var(--color-border-light)', fontSize: '0.9rem', textDecoration: 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-border-light)')}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div>
            <h3 style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.1em', color: 'var(--color-muted-on-dark)', textTransform: 'uppercase', marginBottom: '1rem', margin: '0 0 1rem', textAlign: 'center' }}>Get In Touch</h3>
            <p style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: '1.25rem', textAlign: 'center' }}>
              Ready to get started? Let's talk about your facility's needs.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
              <Link to="/contact" style={{
                display: 'inline-block', background: 'var(--color-primary)', color: 'white',
                padding: '0.65rem 1.5rem', borderRadius: '50px', fontWeight: 700,
                fontSize: '0.9rem', textDecoration: 'none', transition: 'background 0.2s',
                width: '160px', textAlign: 'center',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-primary-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'var(--color-primary)')}>
                Contact Us
              </Link>
              <Link to="/reviews" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })} style={{
                display: 'inline-block', background: 'white', color: 'var(--color-footer-bg)',
                padding: '0.65rem 1.5rem', borderRadius: '50px', fontWeight: 700,
                fontSize: '0.9rem', textDecoration: 'none', transition: 'background 0.2s',
                width: '160px', textAlign: 'center',
              }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                Leave a Review
              </Link>
            </div>
          </div>

        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid var(--color-text-label)', paddingTop: '1.5rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <p style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.8rem', margin: 0 }}>© {new Date().getFullYear()} JVP Health Tech Solutions. All rights reserved.</p>
          {socialLinks.length > 0 && (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                  style={{ color: 'var(--color-muted-on-dark)', transition: 'color 0.2s' }}
                  onMouseEnter={e => (e.currentTarget.style.color = 'white')}
                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-muted-on-dark)')}>
                  <Icon size={30} />
                </a>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <Link to="/privacy-policy" style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.8rem', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-muted-on-dark)')}>Privacy Policy</Link>
            <Link to="/terms" style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.8rem', textDecoration: 'none' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-muted-on-dark)')}>Terms & Conditions</Link>
          </div>
        </div>

        {/* Site credit */}
        <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
          <a href="https://www.ashbservicegroup.com/" target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--color-muted-on-dark)', fontSize: '0.75rem', textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'white')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-muted-on-dark)')}>
            Site by ASHB Service Group
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
